import { useEffect, useState } from "react";
import { Status } from "@repo/database";
import { useTranslation } from "react-i18next";
import { useSwipeable } from "react-swipeable";

import type { CartItem } from "~/providers/cart-provider";
import type { Product } from "~/providers/product-provider";
import { useDragScroll } from "~/hooks/use-drag-scroll";
import { useProduct } from "~/hooks/use-product";
import { useRegisterStore } from "~/stores/use-register-store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { RegisterButton } from "../ui/register-button";
import { ProductButton } from "./product-button";

const SearchInput = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (value: string) => void;
}) => {
  const { t } = useTranslation("common");
  return (
    <div className="flex flex-row">
      <Input
        className="rounded-none h-14 text-lg"
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={t("search")}
        value={value}
      />
      {value && (
        <Button onClick={() => onChange("")} className="rounded-none h-auto">
          Clear
        </Button>
      )}
    </div>
  );
};

export function ProductList({
  onClick,
  products,
  categoryId,
}: {
  onClick?: (item: CartItem) => void;
  products?: Product[];
  categoryId?: string;
}) {
  const [ref] = useDragScroll();

  const [searchInput, setSearchInput] = useState("");

  const { productCategories } = useProduct();

  const { setSelectedCategoryId, selectedCategoryId } = useRegisterStore();

  const swipeHandlers = useSwipeable({
    delta: 30,
    onSwiped: (eventData) => {
      const direction = eventData.dir;
      if (direction === "Left") {
        const prevIndex = productCategories.findIndex(
          (category) => category.id === categoryId,
        );
        if (prevIndex === -1) {
          const firstProductCategory = productCategories[0];
          setSelectedCategoryId(firstProductCategory.id);
          return;
        }
        const nextProductCategory =
          productCategories[prevIndex + 1] ?? productCategories[0];

        setSelectedCategoryId(nextProductCategory.id);
      } else if (direction === "Right") {
        const prevIndex = productCategories.findIndex(
          (category) => category.id === categoryId,
        );
        if (prevIndex === -1) {
          const firstProductCategory = productCategories[0];

          setSelectedCategoryId(firstProductCategory.id);
          return;
        }
        const nextProductCategory =
          productCategories[prevIndex - 1] ??
          productCategories[productCategories.length - 1];

        setSelectedCategoryId(nextProductCategory.id);
      }
    },
  });

  const categoryProducts = categoryId
    ? products
        ?.filter(
          (product) =>
            product.categoryId === categoryId &&
            product.status === Status.active,
        )
        .map((product) => ({
          ...product,
          categoryId,
          position: product.position,
        }))
    : products;

  const lastPositionInCategory =
    categoryProducts?.reduce((acc, curr) => Math.max(acc, curr.position), 0) ??
    0;

  const filteredProducts = products?.filter((product) =>
    product.name.toLowerCase().includes(searchInput.toLowerCase()),
  );

  useEffect(() => {
    setSearchInput("");
  }, [selectedCategoryId]);

  return (
    <div className="flex h-full flex-col overflow-auto" {...swipeHandlers}>
      <div className="shadow border-b-2">
        <SearchInput value={searchInput} onChange={setSearchInput} />
      </div>

      <div
        className="grid hide-scrollbar overflow-auto h-full grid-cols-1 content-start gap-1 px-1 py-3 @xs:grid-cols-2 @lg:grid-cols-3 @2xl:grid-cols-4"
        ref={ref}
      >
        {(searchInput.length > 0 || !categoryId) && (
          <>
            {filteredProducts?.map((product) => (
              <ProductButton
                key={product.id}
                product={product}
                onClick={(item) => {
                  if (onClick) {
                    onClick(item);
                    setSearchInput("");
                  }
                }}
              />
            ))}
          </>
        )}
        {searchInput.length === 0 && categoryId && (
          <>
            {Array.from({ length: lastPositionInCategory }).map((_, index) => {
              const product = categoryProducts?.find(
                (p) => p.position === index + 1,
              );
              if (product) {
                return (
                  <ProductButton
                    key={product.id}
                    product={product}
                    onClick={(item) => {
                      if (onClick) {
                        onClick(item);
                      }
                    }}
                  />
                );
              }
              // return <div key={index} className="min-w-14"></div>;
              return <RegisterButton className="min-h-14" key={index} />;
            })}
          </>
        )}
      </div>
    </div>
  );
}
